import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, ShieldCheck, Zap, TrendingDown, Building2, Users, Check, Star } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import HotelCard from "@/components/HotelCard";
import VerticalSelector from "@/components/VerticalSelector";

const QUICK_CITIES = ["Hyderabad", "Goa", "Bangalore", "Mumbai", "Delhi", "Chennai", "Kolkata", "Jaipur"];

const CITY_TILES = [
  { name: "Mumbai",    img: "https://images.pexels.com/photos/6220533/pexels-photo-6220533.jpeg",   tag: "22 stays" },
  { name: "Delhi",     img: "https://images.pexels.com/photos/34496702/pexels-photo-34496702.jpeg", tag: "18 stays" },
  { name: "Hyderabad", img: "https://images.pexels.com/photos/34496701/pexels-photo-34496701.jpeg", tag: "26 stays" },
  { name: "Bangalore", img: "https://images.pexels.com/photos/14060500/pexels-photo-14060500.jpeg", tag: "31 stays" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [city, setCity] = useState("");
  useSEO({
    title: "Hotels Bid For You",
    description: "India's first AI-powered luxury hotel marketplace. Compare, negotiate & book premium hotels across Mumbai, Delhi, Bangalore, Hyderabad. Save 20-40% on every stay.",
    keywords: "hotel booking, hotel negotiation, luxury hotels india, mumbai hotels, delhi hotels, bangalore hotels, hyderabad hotels, swaanstay",
    url: "https://price-compete.emergent.host/",
  });

  useEffect(() => {
    api.get("/hotels").then(({ data }) => setHotels(data.slice(0, 6))).catch((e) => console.debug("fetch failed:", e?.message));
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const q = city ? `?city=${encodeURIComponent(city)}` : "";
    navigate(`/search${q}`);
  };

  return (
    <div>
      {/* ============================================================
          HERO — Premium black+gold with negotiation showcase card
      ============================================================ */}
      <section className="relative overflow-hidden ss-surface ss-hero-gradient ss-noise">
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-center relative">
          {/* Left: headline + search */}
          <div className="lg:col-span-7 ss-anim-in">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C8A34D]/40 bg-[#C8A34D]/10 mb-6">
              <span className="ss-glow-dot" />
              <span className="ss-label text-[#E8D8A8]">India&apos;s first luxury AI hotel marketplace</span>
            </div>

            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] text-white">
              Where would you<br />
              like to <span className="ss-underline-gold ss-text-gold">stay tonight?</span>
            </h1>

            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed font-body">
              Skip the endless scrolling. Post your stay, watch hotels compete on price with
              <span className="text-[#E8D8A8] font-semibold"> AI-powered negotiation</span>, and pay the hotel directly.
            </p>

            {/* Travel vertical selector — MMT-parity */}
            <div className="mt-8 max-w-2xl">
              <VerticalSelector variant="dark" source="landing" />
            </div>

            {/* Big premium search bar */}
            <form onSubmit={onSearch} className="mt-5 max-w-2xl">
              <div className="ss-card-glass p-2 flex flex-col sm:flex-row gap-2 rounded-2xl">
                <input
                  data-testid="hero-city-input"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Search by city — Goa, Hyderabad, Delhi…"
                  className="flex-1 px-5 py-4 bg-transparent text-white placeholder:text-white/50 font-body font-semibold focus:outline-none rounded-xl"
                />
                <Button
                  type="submit"
                  data-testid="hero-search-btn"
                  className="ss-btn ss-btn-primary rounded-xl px-8 py-4 h-auto text-base"
                >
                  Search stays <ArrowRight className="ml-1 w-4 h-4" />
                </Button>
              </div>

              {/* Quick city chips */}
              <div className="mt-5 flex flex-wrap gap-2" data-testid="hero-quick-cities">
                {QUICK_CITIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => navigate(`/search?city=${encodeURIComponent(c)}`)}
                    data-testid={`hero-quick-city-${c.toLowerCase()}`}
                    className="px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide text-white/80 border border-white/15 bg-white/5 hover:border-[#C8A34D] hover:text-[#E8D8A8] hover:bg-[#C8A34D]/10 transition-all"
                  >
                    {c}
                  </button>
                ))}
              </div>
            </form>

            {/* Trust chips */}
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70 font-body">
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#4ADE80]" /> Free for travellers</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#4ADE80]" /> Pay hotel directly</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#4ADE80]" /> Instant AI quotes</span>
            </div>
          </div>

          {/* Right: signature negotiation card */}
          <div className="lg:col-span-5 ss-anim-in">
            <div className="relative">
              <div className="absolute -inset-6 -z-10 rounded-3xl blur-3xl bg-[#C8A34D]/20" aria-hidden />
              <div className="ss-card ss-elev-gold overflow-hidden">
                <div className="relative">
                  <img
                    src="https://images.pexels.com/photos/34496702/pexels-photo-34496702.jpeg"
                    alt="Featured luxury hotel"
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F1115] via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="ss-chip-gold"><Sparkles className="w-3 h-3" /> AI Negotiation</span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="ss-label text-[#E8D8A8]">Signature stay</div>
                    <div className="font-head text-white font-bold text-xl leading-tight mt-1">The Marigold Heritage</div>
                    <div className="text-white/70 text-xs flex items-center gap-2 mt-1">
                      <Star className="w-3 h-3 fill-[#C8A34D] stroke-[#C8A34D]" /> 4.8 · Delhi
                    </div>
                  </div>
                </div>

                <div className="p-5 space-y-4 bg-[#1A1D24]">
                  {/* Price arrows */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="ss-label text-white/50">Listed price</div>
                      <div className="font-head text-2xl font-black text-white/40 line-through mt-1">₹4,200</div>
                    </div>
                    <ArrowRight className="text-[#C8A34D] w-6 h-6" />
                    <div className="text-right">
                      <div className="ss-label text-[#E8D8A8]">You pay</div>
                      <div className="font-head text-3xl font-black text-white mt-1">₹2,850</div>
                    </div>
                  </div>

                  {/* AI reasoning line */}
                  <div className="rounded-xl border border-[#C8A34D]/25 bg-[#C8A34D]/8 p-3 flex items-start gap-2">
                    <Zap className="w-4 h-4 text-[#C8A34D] shrink-0 mt-0.5" />
                    <p className="text-xs text-white/80 leading-relaxed font-body">
                      <span className="font-semibold text-[#E8D8A8]">AI approved.</span> Occupancy 32% · 18 rooms available · midweek demand moderate.
                    </p>
                  </div>

                  {/* Stat pills */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-xl bg-[#16A34A]/12 border border-[#16A34A]/30 p-2">
                      <div className="ss-label text-[#4ADE80]">Saved</div>
                      <div className="font-head font-black text-white mt-0.5">32%</div>
                    </div>
                    <div className="rounded-xl bg-white/5 border border-white/10 p-2">
                      <div className="ss-label text-white/60">Bids</div>
                      <div className="font-head font-black text-white mt-0.5">7</div>
                    </div>
                    <div className="rounded-xl ss-gold-gradient p-2">
                      <div className="ss-label text-black/70">Deal</div>
                      <div className="font-head font-black text-black mt-0.5">Closed</div>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate("/search")}
                    data-testid="hero-showcase-cta"
                    className="ss-btn ss-btn-primary w-full mt-2"
                  >
                    Try AI Negotiation <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gold hairline */}
        <div className="h-px bg-gradient-to-r from-transparent via-[#C8A34D]/40 to-transparent" />
      </section>

      {/* ============================================================
          FEATURED STAYS — dark surface, gold accents
      ============================================================ */}
      {hotels.length > 0 && (
        <section className="ss-surface py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <div>
                <div className="ss-label ss-text-gold">Trending on SwaanStay</div>
                <h2 className="font-head text-3xl md:text-4xl lg:text-5xl font-black text-white mt-3 leading-tight">
                  Featured stays<br /><span className="ss-text-gold-soft">for tonight</span>
                </h2>
              </div>
              <Link
                to="/search"
                data-testid="featured-view-all"
                className="ss-btn ss-btn-ghost-dark ss-btn-pill text-sm"
              >
                Browse all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 ss-anim-in">
              {hotels.map((h) => (
                <HotelCard key={h.hotel_id} hotel={h} variant="dark" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============================================================
          CITY GRID — dark tile mosaic
      ============================================================ */}
      <section className="ss-surface pb-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="ss-label ss-text-gold">Top destinations</div>
              <h2 className="font-head text-3xl md:text-4xl font-black text-white mt-3">Where deals happen</h2>
            </div>
            <Link to="/search" className="text-[#E8D8A8] text-sm font-semibold hover:underline">Explore all cities →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {CITY_TILES.map((c) => (
              <Link
                key={c.name}
                to={`/search?city=${encodeURIComponent(c.name)}`}
                data-testid={`city-card-${c.name}`}
                className="relative overflow-hidden group block aspect-[4/5] rounded-2xl border border-white/10 hover:border-[#C8A34D] transition-all ss-hover-lift"
              >
                <img src={c.img} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F1115] via-[#0F1115]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="ss-label ss-text-gold">India</div>
                  <div className="font-head text-2xl font-black text-white mt-1">{c.name}</div>
                  <div className="text-xs text-white/60 mt-0.5">{c.tag}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          HOW IT WORKS — light surface, gold ink
      ============================================================ */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="ss-label text-[#A6852F]">How it works</div>
            <h2 className="font-head text-3xl md:text-4xl lg:text-5xl font-black mt-3 leading-[1.1] text-slate-900">
              You stop searching.<br /><span className="text-[#A6852F]">Hotels start bidding.</span>
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed font-body max-w-md">
              Traditional OTAs: hotel sets price, you pay. SwaanStay flips it — post your stay,
              hotels compete with AI-vetted quotes, you pick the best one.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6 ss-anim-in">
            {[
              { n: "01", icon: Building2, title: "Post or pick", desc: "Browse listings or post a custom group request — 'need 15 rooms for a wedding'." },
              { n: "02", icon: Sparkles,  title: "AI bids",     desc: "Verified partners submit AI-vetted quotes and counter your offers in real-time." },
              { n: "03", icon: ShieldCheck, title: "Close the deal", desc: "Chat directly with hotels, lock the deal, pay via UPI. Direct to the hotel." },
            ].map((s) => (
              <div
                key={s.n}
                className="rounded-2xl border border-slate-200 bg-white p-6 ss-hover-glow transition-all"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-[#A6852F]">{s.n}</span>
                  <s.icon className="w-5 h-5 text-[#A6852F]" strokeWidth={2.2} />
                </div>
                <h3 className="font-head text-xl font-bold mt-6 text-slate-900">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed font-body">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          PARTNER CTA — dark black + gold split
      ============================================================ */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-24">
        <div className="rounded-3xl overflow-hidden grid lg:grid-cols-12 ss-elev-gold border border-[#C8A34D]/30">
          <div className="lg:col-span-7 p-10 md:p-14 ss-surface ss-hero-gradient text-white">
            <div className="ss-label ss-text-gold">For hotel partners</div>
            <h2 className="font-head text-3xl md:text-4xl lg:text-5xl font-black mt-3 leading-tight">
              Fill empty rooms.<br />Skip <span className="ss-text-gold-soft">OTA commissions.</span>
            </h2>
            <p className="mt-4 text-white/70 max-w-lg leading-relaxed font-body">
              No commission on bookings — just a flat monthly plan. AI-generated bids on group leads,
              PMS-integrated inventory, direct-to-hotel payments.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => navigate("/partner-cta")} className="ss-btn ss-btn-primary px-6 py-5 h-auto" data-testid="partner-cta-btn">
                List your hotel <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => navigate("/login")} className="ss-btn ss-btn-ghost-dark px-6 py-5 h-auto" data-testid="partner-login-cta">
                I&apos;m a hotel partner
              </Button>
            </div>
          </div>
          <div className="lg:col-span-5 p-10 md:p-14 bg-[#F7F1DF] grid grid-cols-2 gap-6 content-center">
            {[
              ["12K+", "Travellers waiting", TrendingDown],
              ["0%",   "Booking commission", ShieldCheck],
              ["48hr", "Avg deal close",     Zap],
              ["16",   "Cities live",        Users],
            ].map(([n, l, Icon]) => (
              <div key={l}>
                <Icon className="w-5 h-5 text-[#A6852F] mb-2" strokeWidth={2.2} />
                <div className="font-head text-4xl font-black text-slate-900">{n}</div>
                <div className="ss-label text-slate-600 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
